'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import type { GetInteractionAggregatesReqFiltersV2Item } from '@/api/generated/model';
import { resolveMethodologyDimensions, type MethodologyDimension } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import { useAiProficiencyEffectiveNow, useAiProficiencyStableNow } from '@/domains/pages/ai-proficiency/hooks/use-ai-proficiency-stable-now';
import { AI_PROFICIENCY_PAGE_QUERY_OPTIONS, aiProficiencyMethodologyDimensionsQueryKey, getAiProficiencyTimeRangeQueryKey } from '@/domains/pages/ai-proficiency/query-options';
import { METHODOLOGY_AGGREGATE_VARIABLES } from '@/domains/pages/ai-proficiency/utils/aifi-aggregates-api';
import { resolveAiProficiencyAggregateTimeRange } from '@/domains/pages/ai-proficiency/utils/api-payloads';
import { useMemo } from 'react';
type UseMethodologyDrawerDataParams = {
  open: boolean;
  projectId: string | undefined;
  scopeFilters?: GetInteractionAggregatesReqFiltersV2Item[];
  queryKeySuffix?: string;
};
export function useMethodologyDrawerData(t0) {
  const $ = _c(27);
  const {
    open,
    projectId,
    scopeFilters: t1,
    queryKeySuffix: t2
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const scopeFilters = t3;
  const queryKeySuffix = t2 === undefined ? "org" : t2;
  const stableNow = useAiProficiencyStableNow();
  const {
    effectiveNow,
    isReady: isEffectiveNowReady
  } = useAiProficiencyEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  let t4;
  if ($[2] !== effectiveNow || $[3] !== stableNow) {
    t4 = resolveAiProficiencyAggregateTimeRange(stableNow, effectiveNow);
    $[2] = effectiveNow;
    $[3] = stableNow;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const aggregateTimeRange = t4;
  let t5;
  if ($[5] !== aggregateTimeRange) {
    t5 = aggregateTimeRange ? getAiProficiencyTimeRangeQueryKey(aggregateTimeRange) : null;
    $[5] = aggregateTimeRange;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const aggregateTimeRangeQueryKey = t5;
  const enabled = open && Boolean(projectId && aggregateTimeRange && isEffectiveNowReady);
  let t6;
  if ($[7] !== aggregateTimeRangeQueryKey || $[8] !== projectId || $[9] !== queryKeySuffix || $[10] !== scopeFilters) {
    t6 = aiProficiencyMethodologyDimensionsQueryKey(queryKeySuffix, projectId, aggregateTimeRangeQueryKey, scopeFilters);
    $[7] = aggregateTimeRangeQueryKey;
    $[8] = projectId;
    $[9] = queryKeySuffix;
    $[10] = scopeFilters;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== aggregateTimeRange || $[13] !== projectId || $[14] !== scopeFilters) {
    t7 = t8 => {
      const {
        signal
      } = t8;
      return getInteractionAggregates({
        project_id: projectId,
        time_range: aggregateTimeRange,
        filters: [],
        filters_v2: scopeFilters,
        variables: METHODOLOGY_AGGREGATE_VARIABLES,
        limit: 1
      }, {
        signal
      });
    };
    $[12] = aggregateTimeRange;
    $[13] = projectId;
    $[14] = scopeFilters;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== enabled || $[17] !== t6 || $[18] !== t7) {
    t8 = {
      queryKey: t6,
      queryFn: t7,
      enabled,
      ...AI_PROFICIENCY_PAGE_QUERY_OPTIONS
    };
    $[16] = enabled;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const query = useQuery(t8);
  let t9;
  bb0: {
    if (!query.data) {
      t9 = null;
      break bb0;
    }
    let t10;
    if ($[20] !== query.data.data[0]) {
      t10 = resolveMethodologyDimensions(query.data.data[0]);
      $[20] = query.data.data[0];
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    t9 = t10;
  }
  const dimensions = t9;
  const t10 = enabled && query.isLoading;
  let t11;
  if ($[22] !== dimensions || $[23] !== query.error || $[24] !== query.isError || $[25] !== t10) {
    t11 = {
      dimensions,
      isLoading: t10,
      isError: query.isError,
      error: query.error
    };
    $[22] = dimensions;
    $[23] = query.error;
    $[24] = query.isError;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}